import React, { useState } from 'react';
import moment from 'moment';
import { Dropdown } from 'react-bootstrap';
import { DateRange } from 'react-date-range-patched';
import 'react-date-range-patched/styles.scss';
import 'react-date-range-patched/theme/default.scss';
import Modal from 'react-bootstrap/Modal';
import { Doughnut } from 'react-chartjs-2';
import { FaRedoAlt } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';

import CustomButton from '../../component/CustomButton';

import './AiSummaryModal.sass';

const summaryCards = [
	{ label: 'Total Events', value: '54', caption: '18% vs yesterday', type: 'events', trend: 'down' },
	{ label: 'Vehicles Active', value: '78', suffix: '/120', caption: '65% of fleet', type: 'vehicles' },
	{ label: 'Total Drive Time', value: '18h 42m', caption: '6% vs yesterday', type: 'drive', trend: 'up' },
	{ label: 'Total Distance', value: '1,240 mi', caption: '9% vs yesterday', type: 'distance', trend: 'up' },
];
const eventTypes = [
	{ label: 'Speeding', count: 20 },
	{ label: 'Hard Braking', count: 11 },
	{ label: 'Following Distance', count: 8 },
	{ label: 'Rapid Acceleration', count: 7 },
	{ label: 'Driver Fatigue', count: 5 },
	{ label: 'Cell Phone Usage', count: 4 },
	{ label: 'Seat Belt', count: 3 },
];
const eventTypeColors = ['#f42b3f', '#ff7a1a', '#f5b414', '#5fc23f', '#35b6b8', '#3a86d8', '#6567d9', '#8a5bd8', '#1f9f77', '#d65a31'];
const attentionDevices = [
	{ device: '60514', alert: 'SD Card Recording Error', status: 'critical' },
	{ device: '62420', alert: 'Camera Offline', status: 'critical' },
	{ device: '62518', alert: 'SD Card Mount Error', status: 'warning' },
	{ device: '67018', alert: 'SD Card Initialization Error', status: 'warning' },
];

const getWeeklyPeriod = (start = moment().subtract(7, 'days')) => ({ start: moment(start).startOf('day').toDate(), end: moment(start).add(6, 'days').startOf('day').toDate() });

const getEventTypePercent = (count, total) => (total ? Math.round((count / total) * 100) : 0);

const AiSummaryModal = ({ item, onHide }) => {
	const isWeekly = item && item.type === 'weekly';
	const [period, setPeriod] = useState(() => getWeeklyPeriod());
	const [calendarOpen, setCalendarOpen] = useState(false);
	const latestStart = moment().subtract(7, 'days').endOf('day');
	const periodLabel = moment(period.start).format('MMM D') + ' - ' + moment(period.end).format('MMM D, YYYY') + ' (7 days)';
	const totalEvents = eventTypes.reduce((sum, eventType) => sum + eventType.count, 0);
	const eventTypeRows = eventTypes.map((eventType, index) => ({
		...eventType,
		color: eventTypeColors[index % eventTypeColors.length],
		percent: getEventTypePercent(eventType.count, totalEvents),
	}));
	const eventTypeChartData = {
		labels: eventTypeRows.map(eventType => eventType.label),
		datasets: [
			{
				data: eventTypeRows.map(eventType => eventType.count),
				backgroundColor: eventTypeRows.map(eventType => eventType.color),
				hoverBackgroundColor: eventTypeRows.map(eventType => eventType.color),
				borderWidth: 0,
				hoverBorderWidth: 0,
			}
		]
	};
	const eventTypeChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		tooltips: {
			callbacks: {
				label: (tooltipItem, data) => {
					const label = data.labels[tooltipItem.index];
					const count = data.datasets[0].data[tooltipItem.index];
					return `${label}: ${count} (${getEventTypePercent(count, totalEvents)}%)`;
				},
			},
		},
		legend: {
			display: false,
		},
		cutoutPercentage: 62,
	};

	return (
	<Modal
		show
		onHide={onHide}
		centered
		dialogClassName="ai-summary-modal"
	>
		<Modal.Body>
			<div className="ai-summary-modal-header">
				<span className="ai-summary-modal-icon">
					<svg width="26" height="26" viewBox="0 0 24 24" fill="none" focusable="false" role="presentation"><rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="2" /><path d="M8 3v4M16 3v4M4 10h16M8 14h2M12 14h4M8 17h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
				</span>
				<span className="ai-summary-modal-title-block">
					<span className="ai-summary-modal-title">{item ? item.title : ''}</span>
					<span className="ai-summary-modal-subtitle">{isWeekly ? 'AI summary of your fleet activity for this week' : "AI summary of today's fleet activity"}</span>
				</span>
				<button type="button" className="ai-summary-modal-close" aria-label="Close" onClick={onHide}>x</button>
			</div>
			<div className="ai-summary-modal-content">
				<div className="ai-summary-info-bar">
					{isWeekly ? (
                        <div className="ai-summary-reporting-period">
                            <span>Reporting Period</span>
                            <Dropdown show={calendarOpen} onToggle={setCalendarOpen}>
                                <Dropdown.Toggle id="ai-summary-reporting-period" variant="light">
                                    <i className="fa fa-calendar" aria-hidden="true" /> {periodLabel}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="ai-summary-week-calendar">
                                    <div className="ai-summary-calendar-heading">
                                        <strong>Select a 7-day period</strong>
                                        <button type="button" aria-label="Close calendar" onClick={() => setCalendarOpen(false)}>x</button>
                                    </div>
                                    <DateRange
                                        ranges={[{ startDate: period.start, endDate: period.end, key: 'selection' }]}
                                        focusedRange={[0, 0]}
                                        onChange={({ selection }) => {
                                            if (moment(selection.startDate).isSameOrBefore(latestStart)) setPeriod(getWeeklyPeriod(selection.startDate));
                                        }}
                                        months={1}
                                        showDateDisplay={false}
                                        showSelectionPreview={false}
                                        moveRangeOnFirstSelection={false}
                                        maxDate={moment().subtract(1, 'day').endOf('day').toDate()}
                                        disabledDay={date => moment(date).isAfter(latestStart)}
                                        rangeColors={['#087cf0']}
                                    />
                                    <div className="ai-summary-calendar-footer">
                                        <span>Selected: {periodLabel}</span>
                                        <button type="button" onClick={() => setPeriod(getWeeklyPeriod())}>Reset</button>
                                    </div>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    ) : <span className="ai-summary-info-text"><FiInfo aria-hidden="true" size='20px' /> Summary generated at 1:35 PM EST.</span>
					}

					<span className="ai-summary-regenerate">
						<CustomButton variant="primary" prefix="ai-summary-regenerate-btn">
							<FaRedoAlt aria-hidden="true" /> Regenerate Summary
						</CustomButton>
						<span>Uses 1 additional AI request</span>
					</span>
				</div>
				<div className="ai-summary-success-bar">{String.fromCharCode(10003)} Saved summary opened -- no AI request used.</div>
				<div className="ai-summary-stat-list">
					{summaryCards.map(card => (
						<div key={card.type} className={`ai-summary-stat-card ${card.type}`}>
							<span className="ai-summary-stat-heading">
								<span className="ai-summary-stat-icon">
									{card.type === 'events' && (
										<svg width="13" height="13" viewBox="0 0 24 24" fill="none" focusable="false" role="presentation"><path d="M6 5h9l3 3v11H6z" fill="currentColor" /><path d="M15 5v4h4" fill="#fff" opacity=".75" /></svg>
									)}
									{card.type === 'vehicles' && (
										<svg width="13" height="13" viewBox="0 0 24 24" fill="none" focusable="false" role="presentation"><rect x="5" y="5" width="14" height="14" rx="2" fill="currentColor" /><path d="M8 9h8M8 13h5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>
									)}
									{card.type === 'drive' && (
										<svg width="13" height="13" viewBox="0 0 24 24" fill="none" focusable="false" role="presentation"><path d="M12 4a8 8 0 1 0 8 8h-8z" fill="currentColor" /><path d="M12 7v5l3 2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
									)}
									{card.type === 'distance' && (
										<svg width="13" height="13" viewBox="0 0 24 24" fill="none" focusable="false" role="presentation"><path d="M5 8h14l-2 8H3z" fill="currentColor" /></svg>
									)}
								</span>
								<span className="ai-summary-stat-label">{card.label}</span>
							</span>
							<span className="ai-summary-stat-value">{card.value}{card.suffix && <small>{card.suffix}</small>}</span>
							<span className="ai-summary-stat-caption">
								{card.trend && <span className="ai-summary-trend-icon">{card.trend === 'up' ? String.fromCharCode(8593) : String.fromCharCode(8595)}</span>}
								{card.caption}
							</span>
						</div>
					))}
				</div>
				<div className="ai-summary-section-title">{String.fromCharCode(10022)} AI Summary</div>
				<div className="ai-summary-text-card">
					<p>Overall fleet activity is normal so far today.</p>
					<p>Speeding is the most common event type, accounting for 37% of all events.</p>
					<p>Three drivers have generated repeated speeding events and may need coaching.</p>
					<p>Two cameras are offline and require attention.</p>
					<p>No severe incidents have been detected today.</p>
					<div className="ai-summary-recommendation">
						<span className="ai-summary-recommendation-icon">
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" focusable="false" role="presentation"><path d="M9 18h6M10 22h4M8 14.5A6 6 0 1 1 16 14.5c-1.2.8-1.8 1.8-1.9 3h-4.2c-.1-1.2-.7-2.2-1.9-3z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
						</span>
						Recommended Action: Review the three drivers with repeated speeding events.
					</div>
				</div>
				<div className="ai-summary-lower">
					<div className="ai-summary-events">
						<div className="ai-summary-lower-title">Events by Type ({totalEvents})</div>
						<div className="ai-summary-events-content">
							<div className="ai-summary-donut">
								<Doughnut data={eventTypeChartData} options={eventTypeChartOptions} />
								<span>{totalEvents}<small>Total Events</small></span>
							</div>
							<div className="ai-summary-event-list">
								{eventTypeRows.map(eventType => (
									<div key={eventType.label} className="ai-summary-event-row">
										<span className="ai-summary-event-dot" style={{ backgroundColor: eventType.color }} />
										<span>{eventType.label}</span>
										<strong>{eventType.count} ({eventType.percent}%)</strong>
									</div>
								))}
							</div>
						</div>
					</div>
					<div className="ai-summary-attention">
						<div className="ai-summary-lower-title">Devices Requiring Attention (12)</div>
						<div className="ai-summary-device-table">
							<div className="ai-summary-device-head">
								<span>Device</span>
								<span>Primary Alert</span>
							</div>
							<div className="ai-summary-device-body">
								{attentionDevices.map(device => (
									<div key={device.device} className="ai-summary-device-row">
										<span><i className={`ai-summary-device-dot ${device.status}`} />{device.device}</span>
										<span>{device.alert}</span>
									</div>
								))}
							</div>
							<div className="ai-summary-device-more">7 more devices with active alerts</div>
						</div>
					</div>
				</div>
			</div>
			<div className="ai-summary-footer">
				<CustomButton variant="primary-outline" prefix="ai-summary-export-btn">
					<span className="ai-summary-export-icon">{String.fromCharCode(9635)}</span>
					Export PDF
				</CustomButton>
				<span className="ai-summary-footer-note">This saved summary opened with <b>no AI request used.</b></span>
				<CustomButton variant="primary-outline" prefix="ai-summary-close-btn" onClick={onHide}>Close</CustomButton>
			</div>
		</Modal.Body>
	</Modal>
	)
}

export default AiSummaryModal;
